import React, { useCallback, useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { compose, newFormatPrice } from '../../../../services/utilityFunctions';
import withCSRF from '../../../../commonComponents/withCSRF';
import ModalHeader from '../../../../commonComponents/modal/ModalHeader';
import Modal from '../../../../commonComponents/modal/Modal';
import translate from '../../../../commonComponents/translate';
import FormInput from '../../../../commonComponents/forms/FormInput';
import FormLabel from '../../../../commonComponents/forms/FormLabel';
import ModalFooter from '../../../../commonComponents/modal/ModalFooter';
import walletProp, { WALLET_TYPE } from '../../proptype/walletProp';
import Notifications from '../../../../services/notifications';
import WalletIcon from '../wallet-picker/icons/WalletIcon';
import LoggedUserContext from '../../../../contextProviders/LoggedUserContext';
import CryptoCurrencyNetworkProp from '../../../../commonComponents/propTypes/crytoCurrencyNetworkProp';
import NetworkListItem from './NetworkListItem';
import CoinsIcon from '../wallet-picker/icons/CoinsIcon';
import ConfigsContext from '../../../../contextProviders/ConfigsContext';
import getBankFieldLabels from '../../../../services/bankFields';

function WithdrawalModal({
  t,
  _csrf,
  // currencies,
  cryptoCurrenciesNetwork,
  activeWallet,
  onClose,
}) {
  const { type, currencyInfo, userMinAmountWithdraw } = activeWallet;

  const { loggedUser } = useContext(LoggedUserContext);
  const { organization } = useContext(ConfigsContext);

  const { walletConfiguration } =
    organization?.services?.wallet_advanced?.config || {};

  const isCrypto = activeWallet.type === WALLET_TYPE.CRYPTO;

  // Country-aware bank labels (IBAN/SWIFT vs CLABE/Account number) — BRAVON-6285.
  const bankLabels = getBankFieldLabels(
    loggedUser?.address?.countryCode || loggedUser?.shippingCountry,
  );

  const [isSubmitting, setIsSubmitting] = useState(false);

  const fiatBalance = Math.floor(activeWallet.userPriceBalance?.amount || 0);
  const cryptoBalance = activeWallet.balance || 0;
  const balance = isCrypto ? cryptoBalance : fiatBalance;

  const cryptoCurrency =
    activeWallet?.currencyInfo?.symbol_native ||
    activeWallet?.currencyInfo?.symbol;

  const networks = isCrypto
    ? cryptoCurrenciesNetwork
        .map(network => ({
          type: WALLET_TYPE.CRYPTO,
          name: network.networkName,
          code: network.networkCode,
          currency: network.code,
          currencyInfo: network.currencyInfo,
        }))
        .concat({
          type: WALLET_TYPE.FIAT,
          name: loggedUser?.currency,
          code: '',
          currency: loggedUser?.currency,
        })
    : [
        {
          type: WALLET_TYPE.FIAT,
          name: loggedUser?.currency,
          code: '',
          currency: loggedUser?.currency,
        },
      ];

  const initialNetwork = networks[0];

  const userMinAmountWithdrawPrice = userMinAmountWithdraw?.price ?? 0;
  const minWithdrawLabel = isCrypto
    ? `${userMinAmountWithdrawPrice}${cryptoCurrency}`
    : newFormatPrice(
        userMinAmountWithdrawPrice,
        true,
        userMinAmountWithdraw?.code,
      );

  const [form, setForm] = useState({
    date: new Date().valueOf(),
    amount: isCrypto ? balance : (balance / 100).toFixed(2),
    description: '',
    targetPayoutInfo: {
      targetType: initialNetwork?.type || '',
      currency: initialNetwork?.currency,
      IBAN:
        initialNetwork?.type === WALLET_TYPE.CRYPTO
          ? ''
          : loggedUser?.personalInfo?.IBAN?.IBAN || '',
      SWIFT:
        initialNetwork?.type === WALLET_TYPE.CRYPTO
          ? ''
          : loggedUser?.personalInfo?.IBAN?.SWIFT || '',
      cryptoWalletNetworkCode: initialNetwork?.code || '',
      cryptoWalletNetworkName: initialNetwork?.name || '',
      cryptoWalletAddress: initialNetwork
        ? (loggedUser?.personalInfo?.cryptoWalletAddress || [])?.find(
            address =>
              address.network === initialNetwork.code &&
              address.currency === initialNetwork.currency,
          )?.walletAddress || ''
        : '',
    },
  });

  const [errors, setErrors] = useState({});

  const validate = useCallback(
    newValues => {
      const result = {};

      if (newValues.targetPayoutInfo.targetType === WALLET_TYPE.FIAT) {
        result['targetPayoutInfo.IBAN'] = !newValues.targetPayoutInfo.IBAN
          ? t('ERROR_FIELD_REQUIRED')
          : '';
        if (bankLabels.secondaryKey && bankLabels.secondaryRequired) {
          result['targetPayoutInfo.SWIFT'] = !newValues.targetPayoutInfo.SWIFT
            ? t('ERROR_FIELD_REQUIRED')
            : '';
        }
      } else {
        result['targetPayoutInfo.cryptoWalletAddress'] = !newValues
          .targetPayoutInfo.cryptoWalletAddress
          ? t('ERROR_FIELD_REQUIRED')
          : '';
      }

      result.amount = !newValues.amount ? t('ERROR_FIELD_REQUIRED') : '';

      const totalAmount = parseFloat(newValues.amount);
      const amount = isCrypto ? totalAmount : Math.floor(totalAmount * 100);

      if (amount > balance) {
        result.amount = t('commissions.withdrawalModal.amountOverBalance');
      }
      if (amount <= 0) {
        result.amount = t('commissions.withdrawalModal.amountZero');
      }
      console.log('**** amount: ', amount);
      console.log(
        '**** userMinAmountWithdrawPrice: ',
        userMinAmountWithdrawPrice,
      );
      if (amount < userMinAmountWithdrawPrice) {
        result.amount = t(
          'commissions.withdrawalModal.minimumAmountWithdraw',
          minWithdrawLabel,
        );
      }

      setErrors(result);
      return Object.values(result).filter(error => !!error).length === 0;
    },
    [balance, bankLabels.secondaryKey, bankLabels.secondaryRequired],
  );

  const handleChange = useCallback((field, value) => {
    setErrors(old => ({ ...old, [field]: '' }));
    setForm(old => ({ ...old, [field]: value }));
  }, []);

  const handlePayoutInfoChange = useCallback((field, value) => {
    setErrors(old => ({ ...old, [`targetPayoutInfo.${field}`]: '' }));
    setForm(old => ({
      ...old,
      targetPayoutInfo: { ...old.targetPayoutInfo, [field]: value },
    }));
  }, []);

  const handleNetworkChange = useCallback(
    network => {
      const networkIsCrypto = network.type === WALLET_TYPE.CRYPTO;
      setForm(old => ({
        ...old,
        targetPayoutInfo: {
          ...old.targetPayoutInfo,
          targetType: network.type,
          currency: network.currency,
          IBAN: networkIsCrypto
            ? ''
            : loggedUser?.personalInfo?.IBAN?.IBAN || '',
          SWIFT: networkIsCrypto
            ? ''
            : loggedUser?.personalInfo?.IBAN?.SWIFT || '',
          cryptoWalletNetworkCode: networkIsCrypto ? network.code : '',
          cryptoWalletNetworkName: networkIsCrypto ? network.name : '',
          cryptoWalletAddress: networkIsCrypto
            ? (loggedUser?.personalInfo?.cryptoWalletAddress || [])?.find(
                address =>
                  address.network === network.code &&
                  address.currency === network.currency,
              )?.walletAddress || ''
            : '',
        },
      }));
    },
    [loggedUser?.personalInfo?.cryptoWalletAddress],
  );

  const handleWithdraw = useCallback(async () => {
    if (!validate(form)) {
      return;
    }

    const totalAmount = parseFloat(form.amount);
    const amount = isCrypto ? totalAmount : totalAmount * 100;

    try {
      setIsSubmitting(true);
      const url = '/api/analytics/wallet/withdraw';
      await axios.post(
        url,
        {
          amount,
          date: form.date,
          description: form.description,
          type: activeWallet?.type,
          currency: isCrypto ? activeWallet?.currencyInfo?.code : undefined,
          // don't send empty strings to the backend
          targetPayoutInfo: Object.fromEntries(
            Object.entries(form.targetPayoutInfo).filter(entry => !!entry[1]),
          ),
        },
        {
          headers: {
            'Accept-Version': '1.0.0',
            'X-Gamification-API': 'Games',
            'X-CSRF-Token': _csrf,
            _csrf,
          },
        },
      );

      setIsSubmitting(false);
      onClose(true);
      Notifications.showNotificationSuccess(
        t('success'),
        t('withdraw.sucessfull'),
      );
    } catch (error) {
      setIsSubmitting(false);
      console.error(error);
      Notifications.showErrorFromRequest(error);
    }
  }, [form, onClose]);

  const icon = useMemo(() => {
    switch (type) {
      case WALLET_TYPE.CRYPTO:
        return <img src={currencyInfo.icon} alt="crypto logo" />;
      case WALLET_TYPE.FIAT:
        return <WalletIcon />;
      case WALLET_TYPE.COINS:
        return <CoinsIcon />;
      default:
        return null;
    }
  }, [type, currencyInfo]);

  const walletName = useMemo(() => {
    switch (type) {
      case WALLET_TYPE.CRYPTO:
        return currencyInfo?.name;
      default:
        return (
          walletConfiguration?.[type]?.name ||
          t(`commissions.walletType.${type}`)
        );
    }
  }, [type, currencyInfo?.name, walletConfiguration]);

  const iconClassWrapper = useMemo(() => {
    switch (type) {
      case WALLET_TYPE.COINS:
        return 'withdrawal-modal-banner-wallet-value-icon withdrawal-modal-banner-wallet-coins';
      default:
        return 'withdrawal-modal-banner-wallet-value-icon';
    }
  }, [type]);

  const balanceValue = useMemo(() => {
    switch (type) {
      case WALLET_TYPE.CRYPTO:
        return (
          <div className="withdrawal-modal-banner-available-value">
            {`${balance}${cryptoCurrency}`}
          </div>
        );
      case WALLET_TYPE.FIAT:
        return (
          <div className="withdrawal-modal-banner-available-value">
            {newFormatPrice(balance, true, activeWallet.userPriceBalance?.code)}
          </div>
        );
      case WALLET_TYPE.COINS:
        return (
          <div className="withdrawal-modal-banner-available-value withdrawal-modal-banner-coins">
            <span className="coins-value">
              {activeWallet?.coinsBalance || 0} ={' '}
            </span>
            <span className="money-value">
              {newFormatPrice(
                balance,
                true,
                activeWallet.userPriceBalance?.code,
              )}
            </span>
          </div>
        );
      default:
        return '';
    }
  }, [type]);

  const feeWarningTxt = useMemo(() => {
    let msg;
    switch (form?.targetPayoutInfo?.targetType) {
      case WALLET_TYPE.CRYPTO:
        msg = t('commissions.withdrawalModal.feesWarning');
        break;
      case WALLET_TYPE.FIAT:
        msg =
          organization.settings?.payoutWalletFee > 0
            ? t(
                'wallet.fiat.fee.percentage',
                organization.settings?.payoutWalletFee,
              )
            : t('wallet.fiat.fee');
        break;
      default:
        return <span />;
    }

    return (
      <div className="row">
        <div className="col-lg-2 col-12" />
        <div className="col-lg-10 col-12">
          <div className="fees-warning">{msg}</div>
        </div>
      </div>
    );
  }, [
    t,
    form.targetPayoutInfo.targetType,
    organization.settings?.payoutWalletFee,
  ]);

  const hintAmount = t('commissions.minimumAmountWithdraw', minWithdrawLabel);
  return (
    <Modal id="withdrawalModal" onClose={onClose}>
      <div
        className="modal-dialog modal-dialog-centered modal-lg"
        role="document"
      >
        <div className="modal-content">
          <ModalHeader
            title={t('commissions.withdrawalModal.newWithdrawal')}
            onClose={() => onClose(null)}
          />
          <div className="modal-body">
            <div className="container-fluid">
              <div className="portlet-body form">
                <div className="withdrawal-modal-banner">
                  <div className="withdrawal-modal-banner-wallet">
                    <div className="withdrawal-modal-banner-wallet-label">
                      {t('commissions.withdrawalModal.withdrawalFrom')}
                    </div>
                    <div className="withdrawal-modal-banner-wallet-value">
                      <div className={iconClassWrapper}>{icon}</div>
                      <div className="withdrawal-modal-banner-wallet-value-name">
                        {walletName}
                      </div>
                    </div>
                  </div>
                  <div className="withdrawal-modal-banner-available">
                    <div className="withdrawal-modal-banner-available-label">
                      {t('commissions.balance')}
                    </div>
                    {balanceValue}
                  </div>
                </div>
                <div className="row">
                  <div className="col-12">
                    <div className="form-group m-form__group row">
                      <FormLabel
                        id="networkInput"
                        label={`${t('commissions.withdrawalModal.network')}*`}
                        className="col-lg-2 col-12"
                      />
                      <div className="col-lg-10 col-12">
                        <div className="network-container">
                          {networks.map(network => (
                            <NetworkListItem
                              key={`${network.code}_${network.currency}`}
                              onSelect={() => handleNetworkChange(network)}
                              selected={
                                network.currency ===
                                  form.targetPayoutInfo.currency &&
                                network.code ===
                                  form.targetPayoutInfo.cryptoWalletNetworkCode
                              }
                              network={network}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                    {feeWarningTxt}
                  </div>
                </div>
                {form.targetPayoutInfo.targetType === WALLET_TYPE.FIAT ? (
                  <div className="row">
                    <div className="col-12">
                      <FormInput
                        label={`${t(bankLabels.primaryKey)}*`}
                        id="IBAN"
                        name="IBAN"
                        containerClassName="col-lg-10 col-12"
                        labelClassName="col-lg-2 col-12"
                        groupProps={{
                          className: 'form-group m-form__group row',
                        }}
                        value={form.targetPayoutInfo.IBAN}
                        onChange={value =>
                          handlePayoutInfoChange('IBAN', value)
                        }
                        error={errors['targetPayoutInfo.IBAN']}
                      />
                      {bankLabels.secondaryKey ? (
                        <FormInput
                          label={`${t(bankLabels.secondaryKey)}${
                            bankLabels.secondaryRequired ? '*' : ''
                          }`}
                          id="SWIFT"
                          name="SWIFT"
                          containerClassName="col-lg-10 col-12"
                          labelClassName="col-lg-2 col-12"
                          groupProps={{
                            className: 'form-group m-form__group row',
                          }}
                          value={form.targetPayoutInfo.SWIFT}
                          onChange={value =>
                            handlePayoutInfoChange('SWIFT', value)
                          }
                          error={errors['targetPayoutInfo.SWIFT']}
                        />
                      ) : null}
                    </div>
                  </div>
                ) : (
                  <div className="row">
                    <div className="col-12">
                      <FormInput
                        label={t('commissions.withdrawalModal.walletAddress')}
                        id="walletAddress"
                        name="targetPayoutInfo.cryptoWalletAddress"
                        containerClassName="col-lg-10 col-12"
                        labelClassName="col-lg-2 col-12"
                        groupProps={{
                          className: 'form-group m-form__group row',
                        }}
                        value={form.targetPayoutInfo.cryptoWalletAddress}
                        onChange={value =>
                          handlePayoutInfoChange('cryptoWalletAddress', value)
                        }
                        error={errors['targetPayoutInfo.cryptoWalletAddress']}
                      />
                    </div>
                  </div>
                )}
                <div className="row">
                  <div className="col-12">
                    <div
                      className={`form-group m-form__group row ${
                        errors.amount ? 'has-danger' : ''
                      }`}
                    >
                      <FormLabel
                        id="withDrawalDate"
                        label={t('commissions.withdrawalModal.date')}
                        className="col-lg-2 col-12 force-body-color"
                      />
                      <FormInput
                        id="withdrawalDate"
                        type="date"
                        name="date"
                        containerClassName="col-lg-4 col-12"
                        groupProps={{ skipWrapper: true }}
                        value={form.date}
                        disabled
                      />
                      <FormLabel
                        id="withDrawalAmount"
                        label={t('commissions.withdrawalModal.amount')}
                        className="col-lg-2 col-12"
                      />
                      <FormInput
                        id="withdrawalAmount"
                        type="number"
                        name="amount"
                        containerClassName="col-lg-4 col-12"
                        groupProps={{ skipWrapper: true }}
                        value={form.amount}
                        onChange={value => handleChange('amount', value)}
                        error={errors.amount}
                        inputProps={{
                          min: 0,
                          max: balance,
                        }}
                      />
                      {userMinAmountWithdraw &&
                      userMinAmountWithdrawPrice > 0 ? (
                        <span className="minAmountHint">{hintAmount}</span>
                      ) : null}
                    </div>
                  </div>
                  <div className="col-12">
                    <FormInput
                      label={t('commissions.withdrawalModal.description')}
                      id="withdrawalDescription"
                      name="description"
                      containerClassName="col-lg-10 col-12"
                      labelClassName="col-lg-2 col-12"
                      groupProps={{
                        className: 'form-group m-form__group row',
                      }}
                      value={form.description}
                      onChange={value => handleChange('description', value)}
                      hint={t('commissions.withdrawalModal.descriptionHint')}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <ModalFooter
            saveText={t('commissions.withdrawalModal.withdraw')}
            onSave={handleWithdraw}
            disabled={isSubmitting}
            saveIsLoader={isSubmitting}
          />
        </div>
      </div>
    </Modal>
  );
}

WithdrawalModal.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  activeWallet: PropTypes.shape(walletProp.propType).isRequired,
  /* currencies: PropTypes.arrayOf(PropTypes.shape(CurrencyProp.propType))
    .isRequired, */
  cryptoCurrenciesNetwork: PropTypes.arrayOf(
    PropTypes.shape(CryptoCurrencyNetworkProp.propType),
  ).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default compose(translate, withCSRF)(WithdrawalModal);
